/**
 * Module 4: Help & Resources
 * 
 * Knowledge base and support resources
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  BookOpen,
  Sparkles,
  FileText,
  Database,
  MessageCircle,
  ExternalLink,
  HelpCircle
} from 'lucide-react';

const HelpResources = () => {
  return (
    <div className="space-y-6" data-testid="help-resources">
      {/* Quick Links */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass-panel border-white/10 hover:border-[var(--gold)] transition-all cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <BookOpen className="h-8 w-8 text-[var(--gold)]" />
              <h3 className="text-lg font-semibold text-white/90">Getting Started</h3>
            </div>
            <p className="text-sm text-white/70 mb-4">Learn the basics of MedSpaBookings OS</p>
            <Button className="ghost-btn w-full">
              View Guide
              <ExternalLink size={14} className="ml-2" />
            </Button>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/10 hover:border-[var(--gold)] transition-all cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="h-8 w-8 text-[var(--gold)]" />
              <h3 className="text-lg font-semibold text-white/90">Revenue Engine</h3>
            </div>
            <p className="text-sm text-white/70 mb-4">Master lead generation and scoring</p>
            <Button className="ghost-btn w-full">
              View Guide
              <ExternalLink size={14} className="ml-2" />
            </Button>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/10 hover:border-[var(--gold)] transition-all cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="h-8 w-8 text-[var(--gold)]" />
              <h3 className="text-lg font-semibold text-white/90">AI Audits & Proposals</h3>
            </div>
            <p className="text-sm text-white/70 mb-4">Generate compelling content</p>
            <Button className="ghost-btn w-full">
              View Guide
              <ExternalLink size={14} className="ml-2" />
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* FAQ */}
      <Card className="glass-panel border-white/10">
        <CardHeader>
          <CardTitle className="text-white/90 flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-[var(--gold)]" />
            Frequently Asked Questions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible>
            <AccordionItem value="item-1" className="border-white/10">
              <AccordionTrigger className="text-white/90 hover:text-[var(--gold)]">
                How do I import leads?
              </AccordionTrigger>
              <AccordionContent className="text-white/70">
                You can import leads via CSV file upload, manual entry, or through our provider integrations (Outscraper, Apify). Navigate to the Revenue Engine and select your preferred import method.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border-white/10">
              <AccordionTrigger className="text-white/90 hover:text-[var(--gold)]">
                How does AI lead scoring work?
              </AccordionTrigger>
              <AccordionContent className="text-white/70">
                Our AI analyzes multiple data points including business type, location, reviews, website quality, and engagement history to assign a lead score from 0-100. Higher scores indicate better conversion potential.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border-white/10">
              <AccordionTrigger className="text-white/90 hover:text-[var(--gold)]">
                Can I customize audit and proposal templates?
              </AccordionTrigger>
              <AccordionContent className="text-white/70">
                Yes! All AI-generated content is fully editable. You can customize templates, add your branding, and save custom versions for future use.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border-white/10">
              <AccordionTrigger className="text-white/90 hover:text-[var(--gold)]">
                What integrations are available?
              </AccordionTrigger>
              <AccordionContent className="text-white/70">
                We integrate with Stripe (payments), SendGrid (email), Outscraper and Apify (lead generation), and support custom CSV imports. More integrations are coming soon.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border-white/10">
              <AccordionTrigger className="text-white/90 hover:text-[var(--gold)]">
                Is my data secure?
              </AccordionTrigger>
              <AccordionContent className="text-white/70">
                Absolutely. All data is encrypted at rest and in transit. We use MongoDB Atlas for secure database hosting, and all API keys are encrypted. We never share your data with third parties.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card className="glass-panel border-white/10">
        <CardHeader>
          <CardTitle className="text-white/90 flex items-center gap-2">
            <MessageCircle className="h-5 w-5 text-[var(--gold)]" />
            Need More Help?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-white/80">Our support team is here to help you succeed. Get in touch if you have questions or need assistance.</p>
            <div className="flex items-center gap-4">
              <Button className="primary-btn">
                <MessageCircle size={16} />
                Contact Support
              </Button>
              <Button className="ghost-btn">
                <BookOpen size={16} />
                View Documentation
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpResources;
